'use strict'
/**
 * CookieConsentBanner — Bloque B · B.3.3
 *
 * GDPR + ePrivacy cookie consent. Three options + granular personalization
 * modal. Persists to localStorage immediately + POSTs to /api/cookie-consent
 * (best-effort, non-blocking).
 *
 * Props:
 *   onAccepted({ essential, analytics, marketing, personalization })  — fired after persist
 *   force                                                              — bypass the
 *                                                                        skip-if-recent gate
 *                                                                        (for the settings page)
 *
 * Storage:
 *   localStorage['pl_cookie_consent'] = JSON.stringify({
 *     essential, analytics, marketing, personalization, consented_at, version: 1
 *   })
 *   - Banner does NOT show again while value is < 12 months old.
 *
 * Tokens: --prime-* only, zero hardcoded colors, zero native emojis. lucide-react
 * for icons. framer-motion for entrance.
 */

import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Settings as SettingsIcon } from 'lucide-react'

const STORAGE_KEY = 'pl_cookie_consent'
const STORAGE_VERSION = 1
const CONSENT_TTL_MS = 365 * 24 * 3600 * 1000

const CATEGORIES = [
  { key: 'essential',       label: 'Esenciales',       label_en: 'Essential',
    description: 'Sesión, login, seguridad. Necesarias para que la web funcione.',
    description_en: 'Session, login, security. Required for the site to work.',
    required: true },
  { key: 'analytics',       label: 'Analítica',        label_en: 'Analytics',
    description: 'Nos ayuda a entender qué funciona y qué no.',
    description_en: 'Helps us understand what works and what doesn’t.' },
  { key: 'marketing',       label: 'Marketing',        label_en: 'Marketing',
    description: 'Emails no transaccionales y campañas dirigidas.',
    description_en: 'Non-transactional emails and targeted campaigns.' },
  { key: 'personalization', label: 'Personalización',  label_en: 'Personalization',
    description: 'Recordamos tus preferencias (idioma, vistas, A/B asignados).',
    description_en: 'Remembers your preferences (locale, views, A/B assignments).' },
]

function readStored() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed.version !== STORAGE_VERSION) return null
    if (!parsed.consented_at) return null
    if ((Date.now() - new Date(parsed.consented_at).getTime()) > CONSENT_TTL_MS) return null
    return parsed
  } catch (_) {
    return null
  }
}

function persistLocal(payload) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...payload, version: STORAGE_VERSION, consented_at: new Date().toISOString(),
    }))
  } catch (_) {}
}

async function persistRemote(payload, anonymousId) {
  try {
    await fetch('/api/cookie-consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, anonymous_id: anonymousId }),
      credentials: 'include',
    })
  } catch (_) {
    // Best-effort: localStorage is the source of truth for the banner gate.
  }
}

function readOrCreateAnonymousId() {
  if (typeof window === 'undefined') return null
  let id = window.localStorage.getItem('pl_anonymous_id')
  if (!id) {
    id = 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
    window.localStorage.setItem('pl_anonymous_id', id)
  }
  return id
}

export default function CookieConsentBanner({ onAccepted, force = false, locale = 'es' }) {
  const [visible, setVisible] = useState(false)
  const [showCustom, setShowCustom] = useState(false)
  const [choices, setChoices] = useState({
    essential: true, analytics: false, marketing: false, personalization: false,
  })

  const cajaRef = useRef(null)

  useEffect(() => {
    if (force) { setVisible(true); return }
    const stored = readStored()
    if (!stored) setVisible(true)
  }, [force])

  // G5 (retorno-6b) · EL BANNER RESERVA SU SITIO EN VEZ DE TAPAR.
  // Va `position: fixed` abajo con z-index 9999, así que se pinta ENCIMA de lo que haya
  // debajo — y lo que suele haber al final de una pantalla es su botón principal. Medido en
  // el simulador a 390px: el banner cubría el CTA de la landing.
  //
  // Ocultarlo no es una opción: es consentimiento, tiene que verse y tiene que poder
  // decidirse. Lo que no puede es robar el sitio de otro control. Mientras está en pantalla
  // reserva su altura real al final del documento, así que nada queda debajo: el usuario
  // hace scroll y llega a su botón con el banner todavía visible.
  //
  // Se mide en lugar de codificar una altura: el texto envuelve distinto por idioma y por
  // ancho, y "Personalizar" despliega tres filas más. Se restaura lo que hubiera antes.
  useEffect(() => {
    const caja = cajaRef.current
    if (!visible || !caja || typeof window === 'undefined') return undefined
    const cuerpo = document.body
    const previo = cuerpo.style.paddingBottom

    const medir = () => {
      const alto = caja.offsetHeight || 0
      // 16 arriba + 16 abajo son los márgenes del propio banner.
      cuerpo.style.paddingBottom = `${alto + 32}px`
      cuerpo.style.setProperty('--pl-cookie-banner-h', `${alto + 32}px`)
    }
    medir()

    let ro = null
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(medir)
      ro.observe(caja)
    }
    window.addEventListener('resize', medir)

    return () => {
      if (ro) ro.disconnect()
      window.removeEventListener('resize', medir)
      cuerpo.style.paddingBottom = previo
      cuerpo.style.removeProperty('--pl-cookie-banner-h')
    }
  }, [visible, showCustom])

  function commit(payload) {
    persistLocal(payload)
    const anonId = readOrCreateAnonymousId()
    persistRemote(payload, anonId)
    setVisible(false)
    if (typeof onAccepted === 'function') onAccepted(payload)
  }

  function handleAcceptAll() {
    commit({ essential: true, analytics: true, marketing: true, personalization: true })
  }
  function handleEssentialOnly() {
    commit({ essential: true, analytics: false, marketing: false, personalization: false })
  }
  function handleSaveCustom() {
    commit({ ...choices, essential: true })
  }

  const labels = locale === 'en'
    ? { title: 'Cookies & privacy', body: 'We use cookies. Essential ones always run; analytics, marketing and personalization are opt-in.', acceptAll: 'Accept all', essential: 'Essential only', customize: 'Customize', save: 'Save preferences', close: 'Close' }
    : { title: 'Cookies y privacidad', body: 'Usamos cookies. Las esenciales siempre · analítica, marketing y personalización son opt-in.', acceptAll: 'Aceptar todas', essential: 'Solo esenciales', customize: 'Personalizar', save: 'Guardar preferencias', close: 'Cerrar' }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={cajaRef}
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 9999,
            maxWidth: 720, margin: '0 auto',
            background: 'var(--bg-elevated, #FFFFFF)',
            border: '1px solid var(--prime-border-pl, rgba(0,0,0,0.1))',
            borderRadius: 'var(--radius-lg, 12px)',
            padding: '16px 18px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 id="cookie-banner-title" style={{
                margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text)',
              }}>
                {labels.title}
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-soft, #555)', lineHeight: 1.45 }}>
                {labels.body}
              </p>
            </div>
          </div>

          {showCustom && (
            <div data-testid="cookie-consent-custom" style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              {CATEGORIES.map(cat => (
                <label key={cat.key} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px',
                  background: 'color-mix(in srgb, var(--text-muted, #888) 6%, transparent)',
                  borderRadius: 'var(--radius-md, 8px)',
                  cursor: cat.required ? 'default' : 'pointer',
                  opacity: cat.required ? 0.85 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={cat.required ? true : choices[cat.key]}
                    disabled={cat.required}
                    onChange={(e) => setChoices({ ...choices, [cat.key]: e.target.checked })}
                    data-testid={`cookie-category-${cat.key}`}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {locale === 'en' ? cat.label_en : cat.label}
                      {cat.required && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase',
                        }}>{locale === 'en' ? 'required' : 'requerido'}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 2 }}>
                      {locale === 'en' ? cat.description_en : cat.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div style={{
            marginTop: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, justifyContent: 'flex-end',
          }}>
            {!showCustom ? (
              <button
                type="button"
                onClick={() => setShowCustom(true)}
                data-testid="cookie-customize"
                style={btnGhost}
              >
                <SettingsIcon size={13} aria-hidden="true" /> {labels.customize}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSaveCustom}
                data-testid="cookie-save-custom"
                style={btnAccent}
              >
                {labels.save}
              </button>
            )}

            <button
              type="button"
              onClick={handleEssentialOnly}
              data-testid="cookie-essential-only"
              style={btnGhost}
            >
              {labels.essential}
            </button>

            <button
              type="button"
              onClick={handleAcceptAll}
              data-testid="cookie-accept-all"
              style={btnAccent}
            >
              {labels.acceptAll}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  background: 'transparent',
  border: '1px solid var(--prime-border-pl, rgba(0,0,0,0.15))',
  color: 'var(--text)',
  padding: '8px 14px',
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: 13, cursor: 'pointer',
}

const btnAccent = {
  background: 'var(--accent, var(--pl-terracota))',
  border: '1px solid var(--accent, var(--pl-terracota))',
  color: 'var(--bg-elevated, #FFFFFF)',
  padding: '8px 14px',
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
}

// Export for tests
CookieConsentBanner.STORAGE_KEY = STORAGE_KEY
CookieConsentBanner.STORAGE_VERSION = STORAGE_VERSION
CookieConsentBanner.CATEGORIES = CATEGORIES
